import { useAuth } from "./hooks/AuthProvider";
import { Navigate, Route, Routes } from "react-router-dom";
import { CircularProgress, CssBaseline } from "@mui/material";
import { Register } from "./components/Register/Register";
import React, { Suspense } from "react";
import SubscriptionOnboardingDialog from "./components/SubscriptionOnboarding/SubscriptionOnboardingDialog";
import { BackdropFilterDetector } from "./hooks/BackdropFilterDetector";
import AnimatedBackground from "./components/AnimatedBackground/AnimatedBackground";
import {
  usePageTracking,
  useUserTracking,
  useUserIdentification,
} from "./hooks/useAnalytics";

import BottomNav from "./components/BottomNav/BottomNav";
import Dashboard from "./components/Dashboard/Dashboard";
import Header from "./components/Header/Header";
// import Inspiration from './components/Inspiration/Inspiration';
import Integrations from "./components/Integrations/integrations";
import Login from "./components/Login/Login";
import Map from "./components/Map/Map";
import Profile from "./components/Profile/Profile";
import WhatsNew from "./components/WhatsNew/WhatsNew";
import PlanBuilder from "./components/PlanBuilder/PlanBuilder";
import InteractiveMapPlanner from "./components/InteractiveMapPlanner/InteractiveMapPlanner";
import PlanPage from "./components/PlanPage/PlanPage";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import TermsOfService from "./components/legal/TermsOfService";
import Support from "./components/legal/Support";

import "./App.scss";
import EmailWhitelistPage from "./components/Admin/EmailWhitelist/EmailWhitelistPage";

type ProtectedComponentProps<T extends React.ElementType> = {
  component: T;
} & React.ComponentProps<T>;

const ProtectedComponent = <T extends React.ElementType>({
  component: Component,
  ...props
}: ProtectedComponentProps<T>): React.JSX.Element => {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) return <CircularProgress />;
  return isAuthenticated ? (
    <Component {...props} />
  ) : (
    <Navigate to="/login" replace />
  );
};

const App: React.FC = () => {
  const auth = useAuth();

  // Track page views and user properties
  usePageTracking();
  useUserTracking();
  useUserIdentification(
    auth.isAuthenticated ? auth.getAuthTokenClaims()?.sub : null,
  );

  return (
    <>
      <CssBaseline />
      <BackdropFilterDetector />
      <AnimatedBackground>
        <div className="app-container">
          <Header />
          <main className="main-content">
            <Suspense fallback={<CircularProgress />}>
              <Routes>
                <Route
                  path="/"
                  element={<ProtectedComponent component={Dashboard} />}
                />
                <Route
                  path="/login"
                  element={
                    auth.isAuthenticated ? (
                      <Navigate to="/" replace />
                    ) : (
                      <Login />
                    )
                  }
                />
                <Route
                  path="/register"
                  element={
                    auth.isAuthenticated ? (
                      <Navigate to="/" replace />
                    ) : (
                      <Register />
                    )
                  }
                />
                <Route
                  path="/map"
                  element={<ProtectedComponent component={Map} />}
                />
                {/* <Route path="/inspiration" element={<ProtectedComponent component={Inspiration} />} /> */}
                {/* <Route path="/stanton/whitelist" element={<ProtectedComponent component={EmailWhitelistPage} />} /> */}
                <Route
                  path="/stanton/whitelist"
                  element={<EmailWhitelistPage />}
                />
                <Route
                  path="/builder"
                  element={<ProtectedComponent component={PlanBuilder} />}
                />
                <Route
                  path="/profile"
                  element={<ProtectedComponent component={Profile} />}
                />
                <Route
                  path="/settings"
                  element={<Navigate to="/profile" replace />}
                />
                <Route
                  path="/whats-new"
                  element={<ProtectedComponent component={WhatsNew} />}
                />
                <Route
                  path="/integrations"
                  element={<ProtectedComponent component={Integrations} />}
                />
                <Route
                  path="/find"
                  element={
                    <ProtectedComponent component={InteractiveMapPlanner} />
                  }
                />
                <Route path="/plans/:slug" element={<PlanPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/support" element={<Support />} />
              </Routes>
            </Suspense>
          </main>
          <BottomNav />
          <SubscriptionOnboardingDialog />
        </div>
      </AnimatedBackground>
    </>
  );
};
export default App;
